<template>
  <v-list nav dense class="nav-list">
    <!-- Módulo Principal -->
    <v-list-group active-class="module-active" class="module-group" v-model="open">
      <!-- Header del Módulo -->
      <template v-slot:activator>
        <v-list-item-icon class="module-icon">
          <v-icon size="22">{{ icon }}</v-icon>
        </v-list-item-icon>
        <v-list-item-title class="module-title">{{ title }}</v-list-item-title>
      </template>

      <!-- Lista de Items -->
      <div class="items-wrapper">
        <template v-for="(item, index) in processedItems">
          <!-- Items con submenú -->
          <v-list-group v-if="item.children" :key="`group-${index}`" sub-group class="sub-group">
            <template v-slot:activator>
              <div class="d-flex align-center sub-header">
                <v-icon size="20" class="sub-icon" color="rgba(0, 0, 0, 0.6)">{{
                  item.icon
                }}</v-icon>
                <span class="sub-title">{{ item.title }}</span>
              </div>
            </template>

            <v-list-item v-for="(child, childIndex) in processChildren(item.children)"
              :key="`child-${index}-${childIndex}`" :to="child.disabled ? undefined : child.to" :disabled="child.disabled"
              link class="child-item" active-class="child-active">
              <v-icon size="19" class="child-icon" color="rgba(0, 0, 0, 0.6)">{{
                child.icon
              }}</v-icon>
              <v-list-item-title class="child-title">
                {{ child.title }}
              </v-list-item-title>
            </v-list-item>
          </v-list-group>

          <!-- Items regulares -->
          <v-list-item v-else :key="`item-${index}`" :to="item.to" link class="regular-item"
            active-class="regular-active">
            <v-icon size="20" class="regular-icon" color="rgba(0, 0, 0, 0.6)">{{
              item.icon
            }}</v-icon>
            <v-list-item-title class="regular-title">{{ item.title }}</v-list-item-title>
          </v-list-item>
        </template>
      </div>
    </v-list-group>
  </v-list>
</template>

<script>
export default {
  name: "NavModule",

  data() {
    return {
      open: false
    };
  },

  props: {
    icon: {
      type: String,
      required: true,
    },
    title: {
      type: String,
      required: true,
    },
    items: {
      type: Array,
      required: true,
    },
    mini: {
      type: Boolean,
      default: false,
    },
  },

  computed: {
    processedItems() {
      return this.items
        .filter((item) => this.checkFeature(item.feature) && this.checkUser(item.onlyUserId))
        .map((item) => ({
          ...item,
          children: item.children ? this.processChildren(item.children) : undefined,
        }))
        .filter((item) => {
          if (item.children) {
            return item.children.length > 0;
          }
          return true;
        });
    },
  },

  methods: {
    checkFeature(feature) {
      if (!feature) return true;
      return this.$store.getters["auth/globalFeatures"].includes(feature);
    },

    checkUser(onlyUserId) {
      if (!onlyUserId) return true;
      return this.$store.state.auth.user?.user?.user_id === onlyUserId;
    },

    processChildren(children) {
      if (!children) return [];
      return children.filter((child) => this.checkFeature(child.feature) && this.checkUser(child.onlyUserId));
    },
  },

  watch: {
    mini() {
      if (this.mini === true) {
        this.open = false;
      }
    }
  }
};
</script>

<style scoped>
.nav-list {
  padding: 0;
  background: transparent;
}

/* Módulo principal */
.module-group {
  margin: 4px 0;
  border-radius: 12px;
  overflow: hidden;
}

.module-icon {
  min-width: 40px;
  margin-right: 12px !important;
}

.module-title {
  font-size: 14px;
  font-weight: 600;
  letter-spacing: 0.02em;
}

/* Contenedor de items */
.items-wrapper {
  padding: 4px 8px;
  background: rgba(0, 0, 0, 0.02);
  border-radius: 12px;
  margin: 0 4px 8px 4px;
}

/* Submenús */
.sub-group::v-deep .v-list-group__header {
  padding: 0 12px !important;
  min-height: 40px !important;
  border-radius: 8px;
  margin-bottom: 2px;
}

.sub-header {
  width: 100%;
  padding: 4px 0;
}

.sub-icon {
  margin-right: 12px;
  font-size: 20px !important;
}

.sub-title {
  font-size: 13.5px;
  font-weight: 500;
  color: #4A5568;
}

/* Items hijos */
.child-item {
  padding: 0 12px 0 44px !important;
  min-height: 38px !important;
  margin: 2px 0;
  border-radius: 8px;
}

.child-icon {
  margin-right: 12px;
  font-size: 18px !important;
}

.child-title {
  font-size: 13px;
  color: #4A5568;
}

/* Items regulares */
.regular-item {
  padding: 0 12px !important;
  min-height: 44px !important;
  margin: 2px 0;
  border-radius: 8px;
}

.regular-icon {
  margin-right: 12px;
  font-size: 20px !important;
}

.regular-title {
  font-size: 14px;
  font-weight: 500;
  color: #4A5568;
}

/* Estados activos */
.v-list-item--active:not(.module-active) {
  background: rgba(0, 0, 0, 0.05) !important;
}

.v-list-item--active:not(.module-active) .v-icon,
.v-list-item--active:not(.module-active) .v-list-item__title,
.v-list-item--active:not(.module-active) .child-title,
.v-list-item--active:not(.module-active) .regular-title,
.v-list-item--active:not(.module-active) .sub-title {
  color: var(--primary-color) !important;
  font-weight: 600;
}

/* Efectos hover */
.v-list-item:hover:not(.v-list-item--active) {
  background-color: rgba(0, 0, 0, 0.03);
}

/* Ajustes de espaciado */
.v-list-item__icon {
  margin: 4px 0 !important;
  height: 100%;
  display: flex;
  align-items: center;
}

/* Ajustes para el modo mini */
:deep(.v-list-group__header__append-icon) {
  min-width: 24px !important;
  font-size: 18px !important;
}

/* Ajuste de márgenes y padding generales */
:deep(.v-list-group__items) {
  margin-top: 2px !important;
}

.v-list-group--sub-group {
  margin: 2px 0;
}

/* Mejoras en la alineación vertical */
.v-list-item {
  align-items: center;
}

.v-list-item__content {
  padding: 6px 0;
}
</style>
